import Head from 'next/head'
import queryString from 'query-string'
import React, { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import clsx from 'clsx'

import { Text } from '@wartek-id/text'

import { useFeatureFlagContext } from 'utils/hooks/context/useFeatureFlagContext'
import { determineTopicState, shouldHideNav } from '../helper'
import useTopicDetails from 'app/PelatihanMandiriPage/hooks/useTopicDetails'
import AksiNyataCard from '../partials/AksiNyata/AksiNyataCard'
import ModuleCard from '../partials/Module/ModuleCard'
import ModulePlaceholder from '../partials/Module/ModulePlaceholder'
import ProgressionBar from '../partials/Topic/ProgressionBar'
import TopicHeader from '../partials/Topic/TopicHeader'
import GenericList from '../components/GenericList'
import { MILKvKeys, useGetKeyPairValue } from 'api/microlearning/useAPIKeyValue'
import { EmptyState } from 'components/EmptyState'
import WebinarCard from 'app/PelatihanMandiriPage/partials/Webinar/WebinarCard'
import RKMMBanner from '../partials/Topic/RKMMBanner'
import {
  filterByModuleTopicId,
  sortByDate,
} from './TopicDetails.helper.WebinarEntries'
import { useSETrackerEvent } from '../hooks/useSETrackEvent'
import CertificateTopic from '../components/CertificateTopic'
import TopicInfo from '../partials/Topic/TopicInfo'
import CertificateProfileRedirectCard from '../components/CertificateAksiNyataCard'
import TopicDetailsDesktop from './TopicDetails.desktop'
import {
  filterAvailableWebinars,
  getUserEmailFromSession,
} from '../utils/webinar'
import { useLocalStorage } from 'utils/hooks/useLocalStorage'

import type { WebinarEntry } from 'api/microlearning/types'
import type { TopicProgressState } from '../helper'

const TopicDetails = ({
  onPageLoaded,
  onShareClick = () => {},
  isDesktop = false,
}: {
  onPageLoaded?: (topicName?: string) => void
  onShareClick?: () => void
  isDesktop?: boolean
}) => {
  const { query, replace } = useRouter()
  const { topik, filter } = query
  const { from = 'topik', menuId = topik } = query
  const {
    moduleWithProgress,
    topicData,
    fetchStatus,
    progressionData,
    certificateStatus,
  } = useTopicDetails(topik as string)

  const isTopicUpdated = topicData?.isTopicUpdated

  const { getFeatureByKey } = useFeatureFlagContext()
  const isAksiNyataEnabled =
    fetchStatus === 'success' && getFeatureByKey('enableAksiNyata')

  const [isAksiNyataLocked, setIsAksiNyataLocked] = useState(true)
  const isDaljabTopic = topicData?.topicType === 'DALJAB'
  const userSession = useLocalStorage('app-login', undefined)
  const userEmail = getUserEmailFromSession(userSession)
  const webinarKv = useGetKeyPairValue(
    MILKvKeys.WebinarEntries,
    !!fetchStatus,
    {
      select: (data) => {
        return {
          data: sortByDate(
            filterByModuleTopicId(
              filterAvailableWebinars(
                data.data.webinar as WebinarEntry[],
                userEmail
              ),
              parseInt(`${topik}`, 10)
            )
          ).shift(),
          meta: {},
        }
      },
    }
  )

  const { SETrackTopicList } = useSETrackerEvent()
  const selectedModule = moduleWithProgress.find(
    (module) =>
      String(module.id) === String(query.modul) &&
      module.progress?.prerequisiteCompleted
  )

  const handleTracker = (id: string | number) => {
    if (filter) {
      SETrackTopicList({
        action_type: 'choose_module',
        action_filter: filter,
        topic_id: id,
      })
    }
  }

  // Determine if aksinyata is locked or not
  useEffect(() => {
    if (fetchStatus === 'success') {
      const topicState = determineTopicState(progressionData)
      const optNotLocked: TopicProgressState[] = [
        'MODULE_DONE',
        'AKSI_NYATA_CREATED',
        'AKSI_NYATA_ON_PROGRESS',
        'AKSI_NYATA_DONE',
        'AKSI_NYATA_NEED_REVISION',
        'AKSI_NYATA_VALIDATED',
        'AKSI_NYATA_APPEAL',
      ]
      if (optNotLocked.includes(topicState)) {
        setIsAksiNyataLocked(false)
      }

      // redirect if topicType `DALJAB`
      if (isDaljabTopic) {
        replace('/pelatihan-mandiri/404')
      }
    }
    onPageLoaded?.(topicData?.name)
  }, [
    progressionData,
    fetchStatus,
    onPageLoaded,
    topicData?.name,
    isDaljabTopic,
    replace,
  ])

  const TopicContent = () => (
    <>
      <div className="relative">
        <TopicInfo
          isTopicUpdated={isTopicUpdated}
          pointers={topicData?.values}
          shouldShowTab={Boolean(topicData?.contextualizedPageId)}
        />
        <GenericList
          className="relative px-3"
          beforeList={
            <>
              {webinarKv.isSuccess && !!webinarKv.data?.data?.id && (
                <div className="mt-6">
                  <div className="mb-2 flex justify-between">
                    <Text variant="heading-md">Webinar</Text>
                    <Link
                      href={`/${
                        process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                        'pelatihan-mandiri'
                      }/webinar`}
                    >
                      {/* @next-codemod-error This Link previously used the now removed `legacyBehavior` prop, and has a child that might not be an anchor. The codemod bailed out of lifting the child props to the Link. Check that the child component does not render an anchor, and potentially move the props manually to Link. */}
                      <Text as="a" variant="body-sm">
                        <strong className="text-blue-70">
                          Lihat Jadwal Lain
                        </strong>
                      </Text>
                    </Link>
                  </div>
                  <div
                    className={clsx(
                      'mb-4 last:mb-0',
                      isDesktop && 'grid grid-cols-2 gap-2'
                    )}
                  >
                    <WebinarCard
                      id={webinarKv.data.data.id}
                      href={`/${
                        process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                        'pelatihan-mandiri'
                      }/webinar/${webinarKv.data?.data?.id}`}
                      topicName={webinarKv.data.data.topic?.title}
                      topicId={
                        webinarKv.data.data.topic?.id as unknown as string
                      }
                      title={webinarKv.data.data.title}
                      speakers={webinarKv.data.data.speakers}
                      startDate={webinarKv.data.data.startDate}
                      endDate={webinarKv.data.data.endDate}
                    />
                  </div>
                </div>
              )}
              <div className="mb-2 mt-6">
                <Text variant="heading-md">Modul Pelatihan</Text>
                <Text variant="body" color="subdued">
                  Silakan pelajari modul sesuai urutan
                </Text>
              </div>
            </>
          }
          data={moduleWithProgress}
          dataState={isDaljabTopic ? 'loading' : fetchStatus}
          loadingComponent={
            <div className="pt-4">
              <ModulePlaceholder amount={3} />
            </div>
          }
          emptyComponent={
            <EmptyState
              title="Konten belum tersedia"
              description="Belum ada topik yang dapat dipelajari saat ini, silahkan coba lagi nanti."
            />
          }
          renderItem={(
            { id, name, duration, totalMaterial, progress, badgeText },
            index
          ) => (
            <div onClick={() => handleTracker(id)}>
              <ModuleCard
                id={id}
                number={index + 1}
                name={name}
                duration={duration}
                totalMaterial={totalMaterial}
                isLocked={!progress?.prerequisiteCompleted}
                isCompleted={progress?.postTest === 'passed'}
                badgeText={badgeText}
                href={
                  `/${
                    process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                    'pelatihan-mandiri'
                  }/modul/${id}?` +
                  queryString.stringify({
                    topik: query.topik,
                    topik_name: topicData?.name,
                    from,
                    menuId,
                  })
                }
              />
            </div>
          )}
          listKey={({ id }) => id}
          itemWrapperClassName={isDesktop && 'grid grid-cols-2 gap-2'}
        />
      </div>
    </>
  )

  if (isDesktop) {
    return (
      <>
        <Head>
          <title>Pelatihan Mandiri | Topik {topicData?.name}</title>
        </Head>
        <TopicDetailsDesktop
          certificateStatus={certificateStatus}
          fetchStatus={fetchStatus}
          isAksiNyataEnabled={isAksiNyataEnabled}
          isAksiNyataLocked={isAksiNyataLocked}
          menuId={menuId}
          moduleWithProgress={moduleWithProgress}
          onModuleClick={handleTracker}
          onShareClick={onShareClick}
          progressionData={progressionData}
          selectedModuleId={
            selectedModule ? String(selectedModule.id) : undefined
          }
          source={from}
          topicData={topicData}
          topicId={topik as string}
          webinar={webinarKv.data?.data}
        />
      </>
    )
  }

  return (
    <>
      <Head>
        <title>Pelatihan Mandiri | Topik {topicData?.name}</title>
      </Head>
      <div
        className="relative pb-6 bg-gray-10 z-0"
        style={{
          minHeight: shouldHideNav() ? '100vh' : 'calc(100vh - 3.5rem)',
        }}
      >
        {!shouldHideNav() && (
          <div className="sticky top-[54px] z-30">
            <TopicHeader
              title={topicData?.name}
              shouldShowTab={Boolean(topicData?.contextualizedPageId)}
            />

            <div className="hidden">
              <div className="sticky top-[54px] z-10 px-8 py-3 h-24 -mx-3 bg-gray-5">
                <ProgressionBar progressData={progressionData} />
              </div>
              <div className="relative mb-4 -mx-3">
                <hr className="border-subdued" />
              </div>
            </div>
          </div>
        )}
        <TopicContent />
        <CertificateTopic progressData={progressionData} />
        {isAksiNyataEnabled && (
          <div className="relative px-3 z-0">
            <AksiNyataCard
              topikId={query.topik as string}
              name={topicData?.name}
              selectedOptionTitle={
                progressionData?.aksiNyata?.selectedOption?.title
              }
              optionsAmount={topicData?.aksiNyataOptionCount}
              aksiNyataSessionState={progressionData?.aksiNyata?.state}
              isLocked={isAksiNyataLocked}
              aksiNyataId={progressionData?.aksiNyata?.id}
            />
          </div>
        )}
        {!isAksiNyataEnabled && (
          <>
            <div className="relative px-3 my-5 z-0">
              <div className="mb-2">
                <Text variant="heading-md">Aksi Nyata</Text>
              </div>
              <div className="relative flex p-4 w-full bg-white rounded border border-subdued text-sm font-normal">
                <Text variant="body-sm" color="subdued">
                  Anda sudah tidak perlu mengerjakan Aksi Nyata lagi untuk
                  menyelesaikan topik Pelatihan Mandiri.
                </Text>
              </div>
            </div>
          </>
        )}
        <CertificateProfileRedirectCard
          topikId={query.topik as string}
          certificateStatus={certificateStatus}
        />
        <RKMMBanner />
      </div>
    </>
  )
}

export default TopicDetails
